import { useMemo, useState, type ChangeEvent } from "react";

import type {
  ProposeTemplateField,
  ProposeTemplateRepeatableGroup,
  ProposeTemplateTable,
} from "../api/proposeTemplatesApi";

type ProductEntry = {
  id: string;
  tableId: string;
  groupIds: Record<string, string | number>;
  values: Record<string, unknown>;
};

type GroupEntry = {
  groupId: string;
  instanceId: string | number;
  parentIds: Record<string, string | number>;
  values: Record<string, unknown>;
};

type Props = {
  submissionId: number;
  productFields: ProposeTemplateField[];
  repeatableGroups: ProposeTemplateRepeatableGroup[];
  tables: ProposeTemplateTable[];
  products: unknown[];
  onSave: (submissionId: number, products: ProductEntry[]) => Promise<void>;
  onSubmit: (submissionId: number, products: ProductEntry[]) => Promise<void>;
  readOnly?: boolean;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const uniqueId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `product-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const normalizeProducts = (products: unknown[]): ProductEntry[] =>
  products
    .filter((product) => product && typeof product === "object")
    .map((product) => {
      const record = product as Record<string, unknown>;
      const groupIds = asRecord(record.groupIds ?? record.group_ids);
      return {
        id: String(record.id ?? uniqueId()),
        tableId: String(record.tableId ?? record.table_id ?? ""),
        groupIds: Object.fromEntries(
          Object.entries(groupIds).filter((entry): entry is [string, string | number] =>
            typeof entry[1] === "string" || typeof entry[1] === "number",
          ),
        ),
        values: asRecord(record.values),
      };
    });

const createInitialGroupEntries = (
  products: ProductEntry[],
  groups: ProposeTemplateRepeatableGroup[],
): GroupEntry[] => {
  const entries = new Map<string, GroupEntry>();
  const visit = (
    groupList: ProposeTemplateRepeatableGroup[],
    parentIds: Record<string, string | number>,
    product: ProductEntry,
  ) => {
    for (const group of groupList) {
      const instanceId = product.groupIds[group.id];
      if (instanceId !== undefined) {
        const key = `${group.id}|${Object.entries(parentIds).map(([id, value]) => `${id}:${value}`).join("|")}|${instanceId}`;
        if (!entries.has(key)) {
          entries.set(key, {
            groupId: group.id,
            instanceId,
            parentIds,
            values: Object.fromEntries(
              (group.fields ?? []).map((field) => [field.id, product.values[field.id] ?? field.defaultValue ?? ""]),
            ),
          });
        }
        visit(group.children ?? [], { ...parentIds, [group.id]: instanceId }, product);
      }
    }
  };
  products.forEach((product) => visit(groups, {}, product));
  return [...entries.values()];
};

const fieldCanEdit = (field: ProposeTemplateField) =>
  field.editableBy === "seller" && !field.readOnly && !field.calculated;

const NativeField = ({
  field,
  value,
  onChange,
  forceDisabled = false,
}: {
  field: ProposeTemplateField;
  value: unknown;
  onChange: (value: unknown) => void;
  forceDisabled?: boolean;
}) => {
  const disabled = forceDisabled || !fieldCanEdit(field);
  const inputType = field.inputType?.toLowerCase();
  const common = {
    disabled,
    required: field.required,
    value: value == null ? "" : String(value),
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      onChange(event.target.value),
  };

  return (
    <label className="seller-submission-field">
      <span>{field.label}</span>
      {inputType === "textarea" || inputType === "multiline" ? (
        <textarea {...common} rows={2} />
      ) : inputType === "select" || inputType === "dropdown" ? (
        <select {...common}>
          <option value="">選択してください</option>
          {(field.options ?? []).map((option, index) => {
            const item = asRecord(option);
            const optionValue = typeof option === "string" || typeof option === "number"
              ? String(option)
              : String(item.value ?? item.id ?? item.label ?? "");
            const optionLabel = typeof option === "object" && option !== null
              ? String(item.label ?? item.name ?? optionValue)
              : optionValue;
            return <option key={`${optionValue}-${index}`} value={optionValue}>{optionLabel}</option>;
          })}
        </select>
      ) : inputType === "checkbox" || inputType === "boolean" ? (
        <input
          type="checkbox"
          disabled={disabled}
          checked={Boolean(value)}
          onChange={(event) => onChange(event.target.checked)}
        />
      ) : (
        <input
          {...common}
          type={inputType === "number" || inputType === "date" ? inputType : "text"}
        />
      )}
    </label>
  );
};

export const SellerSubmissionProductsEditor = ({
  submissionId,
  productFields,
  repeatableGroups,
  tables,
  products: initialProducts,
  onSave,
  onSubmit,
  readOnly = false,
}: Props) => {
  const fieldsById = useMemo(
    () => new Map(productFields.map((field) => [field.id, field])),
    [productFields],
  );
  const [products, setProducts] = useState(() => normalizeProducts(initialProducts));
  const [groupEntries, setGroupEntries] = useState(() =>
    createInitialGroupEntries(normalizeProducts(initialProducts), repeatableGroups),
  );
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateProductValue = (id: string, fieldId: string, value: unknown) => {
    setProducts((current) => current.map((product) =>
      product.id === id
        ? { ...product, values: { ...product.values, [fieldId]: value } }
        : product,
    ));
  };

  const groupFieldsForIds = (groupIds: Record<string, string | number>) => {
    const values: Record<string, unknown> = {};
    groupEntries.forEach((entry) => {
      if (groupIds[entry.groupId] === entry.instanceId) Object.assign(values, entry.values);
    });
    return values;
  };

  const addProduct = (table: ProposeTemplateTable, groupIds: Record<string, string | number>) => {
    const values = groupFieldsForIds(groupIds);
    (table.fieldIds ?? []).forEach((fieldId) => {
      values[fieldId] = fieldsById.get(fieldId)?.defaultValue ?? "";
    });
    setProducts((current) => [
      ...current,
      { id: uniqueId(), tableId: table.id, groupIds: { ...groupIds }, values },
    ]);
  };

  const removeGroup = (entry: GroupEntry) => {
    setGroupEntries((current) => current.filter((candidate) =>
      candidate.instanceId !== entry.instanceId || candidate.groupId !== entry.groupId,
    ));
    setProducts((current) => current.filter((product) =>
      product.groupIds[entry.groupId] !== entry.instanceId,
    ));
  };

  const addGroup = (
    group: ProposeTemplateRepeatableGroup,
    parentIds: Record<string, string | number>,
  ) => {
    setGroupEntries((current) => [
      ...current,
      {
        groupId: group.id,
        instanceId: uniqueId(),
        parentIds: { ...parentIds },
        values: Object.fromEntries(
          (group.fields ?? []).map((field) => [field.id, field.defaultValue ?? ""]),
        ),
      },
    ]);
  };

  const sameIds = (
    left: Record<string, string | number>,
    right: Record<string, string | number>,
  ) => Object.keys(left).length === Object.keys(right).length &&
    Object.entries(left).every(([key, value]) => right[key] === value);

  const renderTables = (path: string[], groupIds: Record<string, string | number>) =>
    tables
      .filter((table) => JSON.stringify(table.groupPath ?? []) === JSON.stringify(path))
      .map((table) => {
        const fields = (table.fieldIds ?? [])
          .map((fieldId) => fieldsById.get(fieldId))
          .filter((field): field is ProposeTemplateField => Boolean(field));
        const rows = products.filter((product) =>
          product.tableId === table.id && sameIds(product.groupIds, groupIds),
        );
        return (
          <section className="seller-submission-product-table" key={`${table.id}-${path.join("/")}`}>
            <h4>{table.label}</h4>
            {rows.map((row) => (
              <div className="seller-submission-product-row" key={row.id}>
                {fields.map((field) => (
                  <NativeField
                    key={field.id}
                    field={field}
                    value={row.values[field.id] ?? field.defaultValue}
                    onChange={(value) => updateProductValue(row.id, field.id, value)}
                    forceDisabled={readOnly}
                  />
                ))}
                {!readOnly && <button
                  type="button"
                  className="seller-submission-remove-product"
                  onClick={() => setProducts((current) => current.filter((item) => item.id !== row.id))}
                  aria-label="商品行を削除"
                >
                  ×
                </button>}
              </div>
            ))}
            {!readOnly && <button type="button" className="seller-submission-add-product" onClick={() => addProduct(table, groupIds)}>
              商品を追加
            </button>}
          </section>
        );
      });

  const renderGroup = (
    group: ProposeTemplateRepeatableGroup,
    parentIds: Record<string, string | number>,
    parentPath: string[],
  ) => {
    const instances = groupEntries.filter((entry) =>
      entry.groupId === group.id && sameIds(entry.parentIds, parentIds),
    );
    return (
      <section className="seller-submission-repeatable-group" key={`${group.id}-${parentPath.join("/")}`}>
        <div className="seller-submission-group-heading">
          <h3>{group.label}</h3>
          {!readOnly && <button type="button" onClick={() => addGroup(group, parentIds)}>追加</button>}
        </div>
        {instances.map((entry) => {
          const groupIds = { ...parentIds, [group.id]: entry.instanceId };
          const groupPath = [...parentPath, group.id];
          return (
            <div className="seller-submission-group-entry" key={String(entry.instanceId)}>
              <div className="seller-submission-group-entry-heading">
                <strong>{group.label}</strong>
                {!readOnly && <button type="button" onClick={() => removeGroup(entry)} aria-label={`${group.label}を削除`}>×</button>}
              </div>
              {(group.fields ?? []).map((field) => (
                <NativeField
                  key={field.id}
                  field={field}
                  value={entry.values[field.id] ?? field.defaultValue}
                  onChange={(value) => setGroupEntries((current) => current.map((candidate) =>
                    candidate.groupId === entry.groupId && candidate.instanceId === entry.instanceId
                      ? { ...candidate, values: { ...candidate.values, [field.id]: value } }
                      : candidate,
                  ))}
                  forceDisabled={readOnly}
                />
              ))}
              {renderTables(groupPath, groupIds)}
              {(group.children ?? []).map((child) => renderGroup(child, groupIds, groupPath))}
            </div>
          );
        })}
      </section>
    );
  };

  const save = async () => {
    const savedProducts = products.map((product) => ({
      ...product,
      values: { ...groupFieldsForIds(product.groupIds), ...product.values },
    }));
    setSaving(true);
    try {
      await onSave(submissionId, savedProducts);
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const submittedProducts = products.map((product) => ({
      ...product,
      values: { ...groupFieldsForIds(product.groupIds), ...product.values },
    }));
    setSubmitting(true);
    try {
      await onSubmit(submissionId, submittedProducts);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="seller-submission-products-editor">
      <h2>商品入力</h2>
      {renderTables([], {})}
      {repeatableGroups.map((group) => renderGroup(group, {}, []))}
      {!readOnly && <div className="seller-submission-editor-actions">
        <button type="button" className="seller-submission-save" disabled={saving || submitting} onClick={() => void save()}>
          {saving ? "保存中…" : "入力内容を保存"}
        </button>
        <button type="button" className="seller-submission-submit" disabled={saving || submitting} onClick={() => void submit()}>
          {submitting ? "提出中…" : "提出する"}
        </button>
      </div>}
    </section>
  );
};
